// Offline mixdown: rebuilds the mixer graph in an OfflineAudioContext and
// renders the whole project (or a range) to a single AudioBuffer.
import { Project, windowedNotes } from "../state/model";
import { createEffectNode, ensureDenoiseWorklet, ensureDeverbWorklet, chainLatency } from "../audio/effects";
import { createInstrument } from "../audio/gm";

export async function renderMixdown(
  project: Project,
  buffers: Map<string, AudioBuffer>,
  opts: { sampleRate?: number; tailSeconds?: number } = {},
): Promise<AudioBuffer> {
  const sampleRate = opts.sampleRate ?? 48000;
  let length = 0;
  for (const t of project.tracks)
    for (const c of t.clips) length = Math.max(length, c.start + c.duration);
  length += opts.tailSeconds ?? 2;
  length = Math.max(length, 1);
  const baseLen = Math.ceil(length * sampleRate);

  // Plugin-delay-compensation: the denoise insert adds latency. Delay shorter
  // chains so every track aligns, render extra head room, then trim it off so
  // the mix lines up with the timeline (denoised tracks no longer drift late).
  const anySolo = project.tracks.some((t) => t.solo);
  const audibleTracks = project.tracks.filter((t) => !t.mute && (!anySolo || t.solo));

  const tmpCtx = new OfflineAudioContext(1, 1, sampleRate);
  const denoiseReady = await ensureDenoiseWorklet(tmpCtx);
  const deverbReady = await ensureDeverbWorklet(tmpCtx);
  const trackLat = (t: Project["tracks"][number]) => chainLatency(t.fx, denoiseReady, deverbReady);
  const maxTrackLat = audibleTracks.reduce((m, t) => Math.max(m, trackLat(t)), 0);
  const masterLat = chainLatency(project.masterFx, denoiseReady, deverbReady);
  const lead = maxTrackLat + masterLat; // total leading latency to trim afterwards

  const ctx = new OfflineAudioContext(2, baseLen + lead, sampleRate);
  await ensureDenoiseWorklet(ctx);
  await ensureDeverbWorklet(ctx);

  const masterIn = ctx.createGain();
  const masterFader = ctx.createGain();
  masterFader.gain.value = project.masterVolume;
  let mCursor: AudioNode = masterIn;
  for (const fx of project.masterFx) {
    if (fx.kind === "wam" || !fx.enabled) continue;
    const n = createEffectNode(ctx, fx);
    mCursor.connect(n.input); mCursor = n.output;
  }
  mCursor.connect(masterFader);
  masterFader.connect(ctx.destination);

  for (const track of audibleTracks) {
    const fxInput = ctx.createGain();
    const panner = ctx.createStereoPanner();
    panner.pan.value = track.pan;
    const fader = ctx.createGain();
    fader.gain.value = track.volume;
    let cursor: AudioNode = fxInput;
    for (const fx of track.fx) {
      if (fx.kind === "wam" || !fx.enabled) continue;
      const n = createEffectNode(ctx, fx);
      cursor.connect(n.input); cursor = n.output;
    }
    cursor.connect(panner); panner.connect(fader);
    // align this track to the slowest chain
    const comp = (maxTrackLat - trackLat(track)) / sampleRate;
    if (comp > 0) { const dl = ctx.createDelay(comp + 1); dl.delayTime.value = comp; fader.connect(dl); dl.connect(masterIn); }
    else fader.connect(masterIn);

    // instrument track: render its MIDI clips through its instrument into the
    // channel (GM instruments load samples async → wait before scheduling)
    const synth = track.kind === "instrument" && track.instrument ? createInstrument(ctx, track.instrument, buffers) : null;
    if (synth) { synth.output.connect(fxInput); if (synth.ready) await synth.ready; }

    for (const clip of track.clips) {
      if (clip.takeActive === false) continue;
      if (clip.notes) {
        if (synth) for (const n of windowedNotes(clip)) synth.scheduleNote(n.midi, n.vel, clip.start + n.start, n.dur);
        continue;
      }
      const buffer = buffers.get(clip.assetId);
      if (!buffer) continue;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const cg = ctx.createGain();
      const startAt = clip.start;
      cg.gain.setValueAtTime(clip.gain, startAt);
      if (clip.fadeIn > 0) {
        cg.gain.setValueAtTime(0, startAt);
        cg.gain.linearRampToValueAtTime(clip.gain, startAt + clip.fadeIn);
      }
      if (clip.fadeOut > 0) {
        cg.gain.setValueAtTime(clip.gain, Math.max(startAt, startAt + clip.duration - clip.fadeOut));
        cg.gain.linearRampToValueAtTime(0, startAt + clip.duration);
      }
      src.connect(cg); cg.connect(fxInput);
      src.start(startAt, clip.offset, clip.duration);
    }
  }

  const rendered = await ctx.startRendering();
  if (lead === 0) return rendered;
  // drop the leading `lead` samples so denoised tracks align with the timeline
  const out = new AudioBuffer({ numberOfChannels: 2, length: baseLen, sampleRate });
  for (let ch = 0; ch < 2; ch++) out.copyToChannel(rendered.getChannelData(ch).subarray(lead, lead + baseLen), ch);
  return out;
}
